import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardContent, CardActions,
  Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, IconButton, Snackbar, Alert, Chip, Menu, MenuItem,
  ToggleButtonGroup, ToggleButton
} from '@mui/material';
import {
  Add as AddIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FolderOpen as FolderIcon
} from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { auth } from '../firebase';
import { auCatalog } from '../utils/catalogSources';

// `catalog` picks the country's data source (default Australia); the UI is the same.
const Categories = ({ catalog = auCatalog }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  // error state removed; we use snackbar for errors
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [subcategoryDialogOpen, setSubcategoryDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [categoryImageFile, setCategoryImageFile] = useState(null);
  const [categoryImagePreview, setCategoryImagePreview] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [subcategoriesMap, setSubcategoriesMap] = useState({});
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceCounts, setServiceCounts] = useState({ categories: {}, subcategories: {} });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [detailsTab, setDetailsTab] = useState('overview');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsCategory, setDetailsCategory] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCategory, setMenuCategory] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () => setSnackbar({ open: false, message: '', severity: 'success' });

  const fetchServiceCounts = useCallback(async () => {
    try {
      const allServices = await catalog.listAllServices();
      const counts = { categories: {}, subcategories: {} };
      allServices.forEach(service => {
        const data = service || {};
        const cat = data.category || '';
        const sub = data.subcategory || data.subCategory || '';
        if (cat) counts.categories[cat] = (counts.categories[cat] || 0) + 1;
        if (sub) counts.subcategories[sub] = (counts.subcategories[sub] || 0) + 1;
      });
      setServiceCounts(counts);
    } catch (err) {
      console.error('Failed to fetch service counts', err);
    }
  }, [catalog]);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const cats = await catalog.listCategories();
      setCategories(cats);
      fetchServiceCounts();
    } catch (err) {
      console.error('Error fetching categories:', err);
      showSnackbar('Failed to load categories', 'error');
    } finally {
      setLoading(false);
    }
  }, [catalog, fetchServiceCounts]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchSubcategoriesFor = async (categoryId) => {
    try {
      const subs = await catalog.listSubcategories(categoryId);
      setSubcategoriesMap(prev => ({ ...prev, [categoryId]: subs }));
    } catch (err) {
      console.error('Error fetching subcategories for', categoryId, err);
      showSnackbar('Failed to load subcategories', 'error');
    }
  };

  const handleCategoryImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCategoryImageFile(file);
      try { setCategoryImagePreview(URL.createObjectURL(file)); } catch { setCategoryImagePreview(null); }
    }
  };

  const handleOpenAddCategory = () => {
    setCategoryForm({ name: '', description: '' });
    setCategoryImageFile(null);
    setCategoryImagePreview(null);
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) return showSnackbar('Please enter category name', 'error');
    try {
      const data = { name: categoryForm.name, description: categoryForm.description || '', updatedAt: new Date() };
      // handle image upload: store under `categories/{safeName}/...` and overwrite existing file on edit
      if (categoryImageFile) {
        if (!auth || !auth.currentUser) return showSnackbar('Sign in required to upload images', 'error');
        const safeName = (categoryForm.name || 'category').toString().trim().replace(/[^a-zA-Z0-9-_]/g, '_');
        // if editing and previous imagePath exists, reuse it to overwrite; otherwise create new path
        const imagePath = (editingCategory && editingCategory.imagePath) ? editingCategory.imagePath : `categories/${safeName}/category_${Date.now()}_${categoryImageFile.name}`;
        const uploaded = await catalog.uploadCategoryImage(categoryImageFile, imagePath);
        data.imageUrl = uploaded.imageUrl;
        data.imagePath = uploaded.imagePath;
      }
      if (editingCategory) {
        await catalog.updateCategory(editingCategory.id, data);
        setCategories(prev => prev.map(c => c.id === editingCategory.id ? { ...c, ...data } : c));
        setEditingCategory(null);
        showSnackbar('Category updated', 'success');
      } else {
        data.createdAt = new Date();
        const newCategoryId = await catalog.createCategory(data);
        setCategories(prev => [{ id: newCategoryId, ...data }, ...prev]);
        showSnackbar('Category added', 'success');
      }
      setCategoryDialogOpen(false);
      fetchServiceCounts();
    } catch (err) {
      console.error('Error saving category:', err);
      showSnackbar('Failed to save category', 'error');
    }
  };

  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({ name: cat.name || '', description: cat.description || '' });
    setCategoryImagePreview(cat.imageUrl || null);
    setCategoryImageFile(null);
    setCategoryDialogOpen(true);
  };

  const openMenu = (event, cat) => {
    setMenuAnchor(event.currentTarget);
    setMenuCategory(cat);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuCategory(null);
  };
  const handleMenuEdit = () => {
    if (menuCategory) handleEditCategory(menuCategory);
    closeMenu();
  };
  const handleMenuDelete = async () => {
    if (menuCategory) await handleDeleteCategory(menuCategory);
    closeMenu();
  };

  const handleEditSubcategory = (category, sub) => {
    setCurrentCategory(category);
    setEditingSubcategory(sub);
    setCategoryForm({ name: sub.name || '', description: sub.description || '' });
    setCategoryImagePreview(sub.imageUrl || null);
    setCategoryImageFile(null);
    setSubcategoryDialogOpen(true);
  };

  const handleDeleteCategory = async (cat) => {
    if (!cat || !cat.id) return;
    try {
      await catalog.deleteCategory(cat.id);
      setCategories(prev => prev.filter(c => c.id !== cat.id));
      showSnackbar('Category deleted', 'success');
    } catch (err) {
      console.error('Failed to delete category', err);
      showSnackbar('Failed to delete category', 'error');
    }
  };

  const handleDeleteSubcategory = async (category, sub) => {
    if (!category || !category.id || !sub || !sub.id) return;
    try {
      await catalog.deleteSubcategory(category.id, sub.id);
      setSubcategoriesMap(prev => ({ ...prev, [category.id]: (prev[category.id] || []).filter(s => s.id !== sub.id) }));
      showSnackbar('Subcategory deleted', 'success');
    } catch (err) {
      console.error('Failed to delete subcategory', err);
      showSnackbar('Failed to delete subcategory', 'error');
    }
  };

  const handleOpenAddSubcategory = (category) => {
    setCurrentCategory(category);
    setCategoryForm({ name: '', description: '' });
    setCategoryImageFile(null);
    setCategoryImagePreview(null);
    setEditingSubcategory(null);
    setSubcategoryDialogOpen(true);
  };

  const handleSaveSubcategory = async () => {
    if (!currentCategory) return showSnackbar('No category selected', 'error');
    if (!categoryForm.name) return showSnackbar('Please enter subcategory name', 'error');
    try {
      const data = { name: categoryForm.name, description: categoryForm.description || '', createdAt: new Date() };
      // handle subcategory image; store under categories/{categoryName}/subcategories/
      if (categoryImageFile) {
        if (!auth || !auth.currentUser) return showSnackbar('Sign in required to upload images', 'error');
        const safeCat = (currentCategory && currentCategory.name ? currentCategory.name : 'category').toString().trim().replace(/[^a-zA-Z0-9-_]/g, '_');
        const imagePath = (editingSubcategory && editingSubcategory.imagePath) ? editingSubcategory.imagePath : `categories/${safeCat}/subcategories/subcat_${Date.now()}_${categoryImageFile.name}`;
        const uploaded = await catalog.uploadCategoryImage(categoryImageFile, imagePath);
        data.imageUrl = uploaded.imageUrl;
        data.imagePath = uploaded.imagePath;
      }
      if (editingSubcategory) {
        // update existing
        await catalog.updateSubcategory(currentCategory.id, editingSubcategory.id, data);
        setSubcategoriesMap(prev => ({ ...prev, [currentCategory.id]: (prev[currentCategory.id] || []).map(s => s.id === editingSubcategory.id ? { ...s, ...data } : s) }));
        setEditingSubcategory(null);
        showSnackbar('Subcategory updated', 'success');
      } else {
        const newSubcategoryId = await catalog.createSubcategory(currentCategory.id, data);
        setSubcategoriesMap(prev => ({ ...prev, [currentCategory.id]: [{ id: newSubcategoryId, ...data }, ...(prev[currentCategory.id] || [])] }));
        showSnackbar('Subcategory added', 'success');
      }
      setSubcategoryDialogOpen(false);
    } catch (err) {
      console.error('Error saving subcategory:', err);
      showSnackbar('Failed to save subcategory', 'error');
    }
  };

  const filtered = categories.filter(c => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (c.name || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q);
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <div>
          {/* <Typography variant="h5">Categories</Typography>
          <Typography variant="body2" color="text.secondary">Manage service categories</Typography> */}
          <TextField size="small" placeholder="Search categories..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ minWidth: 300 }} />
        </div>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)} size="small">
            <ToggleButton value="list"><ViewListIcon /></ToggleButton>
            <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddCategory}>Add Category</Button>
        </Box>
      </Box>

      {loading ? (
        <Typography>Loading categories...</Typography>
      ) : filtered.length === 0 ? (
        <Card variant="outlined"><CardContent><Typography color="text.secondary">No categories found.</Typography></CardContent></Card>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={2}>
          {filtered.map(cat => (
            <Grid size={{ xs: 12,  md: 6, lg: 4 }} sx={{ display: 'flex' }} key={cat.id}>
              <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Avatar src={cat.imageUrl || ''} sx={{ width: 64, height: 64, backgroundColor: '#eee' }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6">{cat.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{cat.description || 'No description'}</Typography>
                    </Box>
                    <IconButton size="small" onClick={(e) => openMenu(e, cat)}>
                        <MoreVertIcon />
                      </IconButton>
                  </Box>
                </CardContent>
                <CardActions>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                    <Box>
                      <Chip icon={<LocalOfferIcon fontSize="small" />} label={(subcategoriesMap[cat.id] || []).length} size="small" sx={{ mr: 1 }} />
                      <Chip icon={<Inventory2Icon fontSize="small" />} label={(serviceCounts.categories && serviceCounts.categories[cat.name]) || 0} size="small" color="success" sx={{ mr: 1 }} />
                      <Button size="small" startIcon={<FolderIcon />} onClick={() => handleOpenAddSubcategory(cat)}>Add Subcategory</Button>
                      <Button size="small" onClick={async () => { await fetchSubcategoriesFor(cat.id); setDetailsCategory(cat); setDetailsTab('overview'); setDetailsDialogOpen(true); }}>View</Button>
                    </Box>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filtered.map(cat => (
            <Card key={cat.id} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar src={cat.imageUrl || ''} sx={{ width: 56, height: 56, backgroundColor: '#eee' }} />
                    <Box>
                      <Typography variant="h6">{cat.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{cat.description || 'No description'}</Typography>
                    </Box>
                  </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip icon={<LocalOfferIcon fontSize="small" />} label={(subcategoriesMap[cat.id]|| []).length} size="small" />
                    <Chip icon={<Inventory2Icon fontSize="small" />} label={(serviceCounts.categories && serviceCounts.categories[cat.name]) || 0} size="small" color="success" />
                    <Button size="small" startIcon={<FolderIcon />} onClick={() => handleOpenAddSubcategory(cat)}>Add Subcategory</Button>
                    <Button size="small" onClick={async () => { await fetchSubcategoriesFor(cat.id); setDetailsCategory(cat); setDetailsTab('overview'); setDetailsDialogOpen(true); }}>View</Button>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditCategory(cat)}>Edit</Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} disabled>Delete</Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Details Dialog (modal) */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar src={detailsCategory?.imageUrl || ''} sx={{ width: 64, height: 64 }} />
          <Box>
            <Typography variant="h6">{detailsCategory?.name}</Typography>
            <Typography variant="body2" color="text.secondary">Category Details and Related Items</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup value={detailsTab} exclusive size="small" onChange={(e, v) => v && setDetailsTab(v)}>
              <ToggleButton value="overview">Overview</ToggleButton>
              <ToggleButton value="subcategories">Subcategories ({(subcategoriesMap[detailsCategory?.id] || []).length})</ToggleButton>
                <ToggleButton value="services">Services ({(serviceCounts.categories && serviceCounts.categories[detailsCategory?.name]) || 0})</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {detailsTab === 'overview' && (
            <Box>
              <Box sx={{ bgcolor: '#f5f7fa', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Description</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{detailsCategory?.description || 'No description'}</Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Card sx={{ flex: 1, bgcolor: '#eef6ff' }}>
                  <CardContent>
                    <Typography variant="body2" color="primary">Subcategories</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{(subcategoriesMap[detailsCategory?.id] || []).length}</Typography>
                  </CardContent>
                </Card>
                    <Card sx={{ flex: 1, bgcolor: '#ecf9f0' }}>
                  <CardContent>
                    <Typography variant="body2" color="success.main">Services</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{(serviceCounts.categories && serviceCounts.categories[detailsCategory?.name]) || 0}</Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}

          {detailsTab === 'subcategories' && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Category Subcategories</Typography>
              {(subcategoriesMap[detailsCategory?.id] && subcategoriesMap[detailsCategory?.id].length > 0) ? (
                subcategoriesMap[detailsCategory.id].map(sc => (
                  <Card key={sc.id} variant="outlined" sx={{ mb: 1 }}>
                    <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={sc.imageUrl || ''} sx={{ width: 40, height: 40, backgroundColor: '#fafafa' }} />
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{sc.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{sc.description || ''}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={`${(serviceCounts.subcategories && serviceCounts.subcategories[sc.name]) || 0} services`} size="small" />
                        <IconButton size="small" color="primary" onClick={() => handleEditSubcategory(detailsCategory, sc)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteSubcategory(detailsCategory, sc)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No subcategories</Typography>
              )}
            </Box>
          )}

          {detailsTab === 'services' && (
            <Box>
              <Typography variant="subtitle2">Category Services</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Direct Services</Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">Showing {(serviceCounts.categories && serviceCounts.categories[detailsCategory?.name]) || 0} services for this category.</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => { setDetailsDialogOpen(false); handleEditCategory(detailsCategory); }} sx={{ backgroundColor: '#0f172a' }}>Edit Category</Button>
        </DialogActions>
      </Dialog>

        {/* 3-dot menu for edit/delete */}
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
          <MenuItem onClick={handleMenuEdit}><EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit</MenuItem>
          <MenuItem onClick={handleMenuDelete} disabled><DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete</MenuItem>
        </Menu>

      {/* Add Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="span">+ Add New Category</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Create a new service category</Typography>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12}}>
                <TextField
                  fullWidth
                  size="small"
                  label="Category Name *"
                  placeholder="Enter category name"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  placeholder="Enter category description"
                  multiline
                  rows={4}
                  sx={{ mt: 2 }}
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                  helperText="A brief description of what this category represents"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar src={categoryImagePreview || ''} sx={{ width: 72, height: 72, backgroundColor: '#f3f3f3' }} />
                    <Box>
                      <input accept="image/*" id="category-image-input" type="file" style={{ display: 'none' }} onChange={handleCategoryImageChange} />
                      <label htmlFor="category-image-input">
                        <Button component="span" variant="outlined" size="small">Choose File</Button>
                      </label>
                      <Typography variant="caption" color="text.secondary" display="block">{categoryImageFile ? categoryImageFile.name : 'No file chosen'}</Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">Upload an image that represents this category (PNG, JPG or GIF)</Typography>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCategory} sx={{ backgroundColor: '#0f172a' }}>Add Category</Button>
        </DialogActions>
      </Dialog>

      {/* Add Subcategory Dialog */}
      <Dialog open={subcategoryDialogOpen} onClose={() => setSubcategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>+ Add Subcategory{currentCategory ? ` to ${currentCategory.name}` : ''}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Create a new subcategory{currentCategory ? ` for ${currentCategory.name}` : ''}.</Typography>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12}}>
                <TextField fullWidth size="small" label="Name *" placeholder="Enter subcategory name" value={categoryForm.name} onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))} required />
                <TextField fullWidth size="small" label="Description" placeholder="Enter a description" multiline rows={4} sx={{ mt: 2 }} value={categoryForm.description} onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={1}>
                  <Avatar src={categoryImagePreview || ''} sx={{ width: 72, height: 72, backgroundColor: '#f3f3f3' }} />
                  <input accept="image/*" id="subcategory-image-input" type="file" style={{ display: 'none' }} onChange={handleCategoryImageChange} />
                  <label htmlFor="subcategory-image-input">
                    <Button component="span" variant="outlined" size="small">Choose File</Button>
                  </label>
                  <Typography variant="caption" color="text.secondary">{categoryImageFile ? categoryImageFile.name : 'No file chosen'}</Typography>
                  <Typography variant="caption" color="text.secondary">Drag and drop or click to upload</Typography>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubcategoryDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSubcategory} sx={{ backgroundColor: '#0f172a' }}>Add Subcategory</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ mt: 8 }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Categories;
